import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  QrCode, Users, Droplets, TrendingUp, IndianRupee,
  AlertTriangle, Shield, CreditCard,
} from "lucide-react";
import DistributorMasterLayout from "../../../layouts/distributor/DistributorMasterLayout";
import "../../../assets/styles/distributor/DistributorDashboard.css";

/* =============================================
   DATA
   ============================================= */
const WEEKLY_DELIVERIES = [
  { day: "Mon", liters: 120 },
  { day: "Tue", liters: 130 },
  { day: "Wed", liters: 125 },
  { day: "Thu", liters: 145 },
  { day: "Fri", liters: 128 },
  { day: "Sat", liters: 118 },
  { day: "Sun", liters: 95  },
];

const MONTHLY_REVENUE = [
  { month: "Jan", revenue: 45000 },
  { month: "Feb", revenue: 52000 },
  { month: "Mar", revenue: 48000 },
  { month: "Apr", revenue: 63000 },
  { month: "May", revenue: 55000 },
  { month: "Jun", revenue: 67000 },
];

const PAYMENT_ALERTS = [
  { name: "Ahmed Raza",  amount: "PKR 3,600", badge: "Overdue 5 days", type: "overdue"  },
  { name: "Zainab Shah", amount: "PKR 2,400", badge: "Due today",      type: "today"    },
  { name: "Bilal Mirza", amount: "PKR 4,200", badge: "Due in 3 days",  type: "upcoming" },
];

const DISPUTES = [
  { name: "Ali Ahmed", sub: "Short delivery • Today",    badge: "Open",        type: "open"       },
  { name: "Sara Khan", sub: "Late delivery • Yesterday", badge: "In Progress", type: "inprogress" },
];

const SUPPLIERS_SUMMARY = [
  { name: "Farmer Abdul",       received: "500L", due: "PKR 35,000", dueRaw: 35000, status: "pending" },
  { name: "Green Valley Dairy", received: "300L", due: "PKR 0",      dueRaw: 0,     status: "paid"    },
  { name: "Punjab Milk Farm",   received: "200L", due: "PKR 16,000", dueRaw: 16000, status: "overdue" },
];

/* =============================================
   SVG BAR CHART — Weekly Deliveries (green)
   ============================================= */
const BarChartSVG = ({ data }) => {
  const [tooltip, setTooltip] = useState(null);
  const W = 500, H = 200;
  const padL = 38, padR = 10, padT = 10, padB = 28;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const maxVal = 160;
  const yTicks = [0, 40, 80, 120, 160];
  const barW = chartW / data.length;
  const barPad = barW * 0.3;

  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow: "visible", display: "block" }}>
        <defs>
          <filter id="bs">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.1"/>
          </filter>
        </defs>
        {yTicks.map((t) => {
          const y = padT + chartH - (t / maxVal) * chartH;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#eff2f8" strokeWidth="1" strokeDasharray="3 3"/>
              <text x={padL - 5} y={y + 4} textAnchor="end" fontSize="10" fill="#344256" opacity="0.5">{t}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const bH = (d.liters / maxVal) * chartH;
          const x = padL + i * barW + barPad / 2;
          const y = padT + chartH - bH;
          const w = barW - barPad;
          const hovered = tooltip?.i === i;
          return (
            <g key={d.day}
              onMouseEnter={() => setTooltip({ i, x: x + w / 2, y, label: d.day, value: d.liters })}
              onMouseLeave={() => setTooltip(null)}
              style={{ cursor: "pointer" }}
            >
              <rect x={x} y={y} width={w} height={bH} fill="#4caf50" rx="4" opacity={hovered ? 1 : 0.82}/>
              <text x={x + w / 2} y={padT + chartH + 16} textAnchor="middle" fontSize="11" fill="#344256" opacity="0.55">{d.day}</text>
            </g>
          );
        })}
        {tooltip && (
          <g>
            <rect x={tooltip.x - 46} y={tooltip.y - 34} width={92} height={26}
              rx="6" fill="#fff" stroke="#eef2f8" strokeWidth="1.5" filter="url(#bs)"/>
            <text x={tooltip.x} y={tooltip.y - 17} textAnchor="middle" fontSize="11" fontWeight="700" fill="#344256">
              {tooltip.label}: {tooltip.value}L
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

/* =============================================
   SVG LINE CHART — Monthly Revenue (blue)
   ============================================= */
const LineChartSVG = ({ data }) => {
  const [tooltip, setTooltip] = useState(null);
  const W = 500, H = 200;
  const padL = 46, padR = 12, padT = 10, padB = 28;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const maxVal = 80000;
  const yTicks = [0, 20000, 40000, 60000, 80000];

  const pts = data.map((d, i) => ({
    x: padL + (i / (data.length - 1)) * chartW,
    y: padT + chartH - (d.revenue / maxVal) * chartH,
    ...d,
  }));
  const lineD = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaD = `${lineD} L${pts[pts.length - 1].x},${padT + chartH} L${pts[0].x},${padT + chartH} Z`;

  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow: "visible", display: "block" }}>
        <defs>
          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3e96e7" stopOpacity="0.18"/>
            <stop offset="100%" stopColor="#3e96e7" stopOpacity="0.01"/>
          </linearGradient>
          <filter id="ls">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.1"/>
          </filter>
        </defs>
        {yTicks.map((t) => {
          const y = padT + chartH - (t / maxVal) * chartH;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#eff2f8" strokeWidth="1" strokeDasharray="3 3"/>
              <text x={padL - 5} y={y + 4} textAnchor="end" fontSize="10" fill="#344256" opacity="0.5">
                {t === 0 ? "0" : `${t / 1000}k`}
              </text>
            </g>
          );
        })}
        <path d={areaD} fill="url(#revGrad)"/>
        <path d={lineD} fill="none" stroke="#3e96e7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={tooltip?.i === i ? 6 : 4}
            fill="#fff" stroke="#3e96e7" strokeWidth="2"
            onMouseEnter={() => setTooltip({ i, x: p.x, y: p.y, label: p.month, value: p.revenue })}
            onMouseLeave={() => setTooltip(null)}
            style={{ cursor: "pointer" }}/>
        ))}
        {pts.map((p, i) => (
          <text key={i} x={p.x} y={padT + chartH + 16} textAnchor="middle" fontSize="11" fill="#344256" opacity="0.55">{p.month}</text>
        ))}
        {tooltip && (
          <g>
            <rect x={tooltip.x - 62} y={tooltip.y - 34} width={124} height={26}
              rx="6" fill="#fff" stroke="#eef2f8" strokeWidth="1.5" filter="url(#ls)"/>
            <text x={tooltip.x} y={tooltip.y - 17} textAnchor="middle" fontSize="11" fontWeight="700" fill="#344256">
              {tooltip.label}: PKR {tooltip.value.toLocaleString()}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

/* =============================================
   DASHBOARD CONTENT
   ============================================= */
const DashboardContent = () => {
  const navigate = useNavigate();

  return (
    <div className="distDash-page">

      {/* ── Header ─────────────────────────────── */}
      <div className="distDash-header-row">
        <div className="distDash-title">
          <h2>Distributor Dashboard</h2>
          <p>Welcome back! Here's your daily overview.</p>
        </div>
        {/* Scan QR → Milk Diary */}
        <button
          className="distDash-scan-btn"
          onClick={() => navigate("/distributor/milk-diary")}
        >
          <QrCode size={16}/> Scan QR Code
        </button>
      </div>

      {/* ── Stats ──────────────────────────────── */}
      <div className="distDash-stats-row">
        <div className="distDash-stat-card">
          <div className="distDash-stat-left">
            <span>Total Customers</span>
            <h3>7</h3>
            {/* <div className="distDash-stat-sub">↗ +3 this week</div> */}
          </div>
          <div className="distDash-stat-icon green"><Users size={18}/></div>
        </div>
        <div className="distDash-stat-card">
          <div className="distDash-stat-left">
            <span>Today's Deliveries</span>
            <h3>12</h3>
            <div className="distDash-stat-sub neutral">↗ 5 remaining</div>
          </div>
          <div className="distDash-stat-icon blue"><Droplets size={18}/></div>
        </div>
        <div className="distDash-stat-card">
          <div className="distDash-stat-left">
            <span>Monthly Revenue</span>
            <h3>PKR 2000</h3>
            {/* <div className="distDash-stat-sub">↗ +12% vs last month</div> */}
          </div>
          {/* <div className="distDash-stat-icon teal"><TrendingUp size={18}/></div> */}
        </div>
        <div className="distDash-stat-card">
          <div className="distDash-stat-left">
            <span>Pending Payments</span>
            <h3>PKR 1000</h3>
            <div className="distDash-stat-sub neutral">↗ 2 customers</div>
          </div>
          <div className="distDash-stat-icon orange"><IndianRupee size={18}/></div>
        </div>
      </div>

      {/* ── Missing Deliveries Alert ───────────── */}
      {/* <div className="distDash-alert-card">
        <div className="distDash-alert-header">
          <AlertTriangle size={16} style={{ stroke: "#dc2626" }}/>
          <span>Missing Deliveries Alert</span>
        </div>
        {[
          { name: "Kamran Yousuf", addr: "House 12, DHA Phase 5", badge: "Last: 2 days ago" },
          { name: "Nadia Butt",    addr: "Apt 3, Gulberg III",    badge: "Last: 3 days ago" },
        ].map((a, i) => (
          <div className="distDash-alert-row" key={i}>
            <div>
              <span className="distDash-alert-name">{a.name}</span>
              <span className="distDash-alert-addr">{a.addr}</span>
            </div>
            <span className="distDash-alert-badge">{a.badge}</span>
          </div>
        ))}
      </div> */}

      {/* ── Subscription Bar ───────────────────── */}
      <div className="distDash-sub-card">
        <div className="distDash-sub-left">
          <div className="distDash-sub-icon"><Shield size={18}/></div>
          <div className="distDash-sub-info">
            <strong>My Subscription — Basic Plan</strong>
            <span>18/30 customer slots used • Renews in 15 days (Apr 15, 2024)</span>
          </div>
        </div>
        {/* Manage → Subscription */}
        <button
          className="distDash-sub-manage-btn"
          onClick={() => navigate("/distributor/subscription")}
        >
          Manage
        </button>
      </div>

      {/* ── Charts ─────────────────────────────── */}
      <div className="distDash-charts-row">
        <div className="distDash-chart-card">
          <div className="distDash-chart-title">Weekly Deliveries (Liters)</div>
          <BarChartSVG data={WEEKLY_DELIVERIES}/>
        </div>
        <div className="distDash-chart-card">
          <div className="distDash-chart-title">Monthly Revenue (PKR)</div>
          <LineChartSVG data={MONTHLY_REVENUE}/>
        </div>
      </div>

      {/* ── Payment Alerts + Latest Disputes ───── */}
      <div className="distDash-bottom-row">

        {/* Payment Alerts → Payments screen */}
        <div className="distDash-section-card">
          <div className="distDash-section-header">
            <div className="distDash-section-header-left">
              <CreditCard size={16} style={{ stroke: "#ef4444" }}/>
              <span>Payment Alerts</span>
            </div>
            <button
              className="distDash-view-all"
              onClick={() => navigate("/distributor/payments")}
            >
              View All
            </button>
          </div>
          {PAYMENT_ALERTS.map((p, i) => (
            <div className="distDash-pay-row" key={i}>
              <div>
                <span className="distDash-pay-name">{p.name}</span>
                <span className="distDash-pay-amount">{p.amount}</span>
              </div>
              <span className={`distDash-pay-badge ${p.type}`}>{p.badge}</span>
            </div>
          ))}
        </div>

        {/* Latest Disputes → Disputes & Support screen */}
        <div className="distDash-section-card">
          <div className="distDash-section-header">
            <div className="distDash-section-header-left">
              <AlertTriangle size={16} style={{ stroke: "#f97316" }}/>
              <span>Latest Disputes</span>
            </div>
            <button
              className="distDash-view-all"
              onClick={() => navigate("/distributor/disputes")}
            >
              View All
            </button>
          </div>
          {DISPUTES.map((d, i) => (
            <div className="distDash-dispute-row" key={i}>
              <div>
                <span className="distDash-dispute-name">{d.name}</span>
                <span className="distDash-dispute-sub">{d.sub}</span>
              </div>
              <span className={`distDash-dispute-badge ${d.type}`}>{d.badge}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Supplier Payment Summary ────────────── */}
      

    </div>
  );
};

/* =============================================
   EXPORT
   ============================================= */
const DistributorDashboard = () => (
  <DistributorMasterLayout>
    <DashboardContent/>
  </DistributorMasterLayout>
);

export default DistributorDashboard;